import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { Exercise, WorkoutSet } from '../../types';
import { WorkoutList } from './WorkoutList';
import { WorkoutEntryDialog, WorkoutEntryFormState } from './WorkoutEntryDialog';
import { AddBodyWeightDialog } from '../bodyweight/AddBodyWeightDialog';
import { AnalysisDialog } from './AnalysisDialog';
import { AnalysisPopup } from './AnalysisPopup';
import { CloneWorkoutsDialog } from './CloneWorkoutsDialog';
import { ImportRoutineDialog } from './ImportRoutineDialog';
import { RecommendationDialog } from './RecommendationDialog';
import { WorkoutStatsSection } from './calendar/WorkoutStatsSection';
import { WorkoutCalendar } from './calendar/WorkoutCalendar';
import { WorkoutActionSection } from './calendar/WorkoutActionSection';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useExercise } from '../../contexts/ExerciseContext';
import { useWorkout } from '../../contexts/WorkoutContext';
import { useBodyWeight } from '../../contexts/BodyWeightContext';
import { API_URL, getDateKey } from '../../utils';
import { ProgramApiService, ProgramSessionForDate } from '../../services/api/ProgramApiService';
import { ProgramSessionCard, CalendarWorkoutWidget } from '../programs';
import { useActiveWorkout } from '../../hooks/useActiveWorkout';

const defaultNewWorkoutFormState: WorkoutEntryFormState = {
  exercise: '',
  comment: '',
  details: [{ series: '', reps: '', weight: '' }],
  workout_type: 'traditional',
  amrap_time_minutes: '',
  amrap_details: []
};

interface CalendarViewProps {
  setView: (view: 'calendar' | 'summary' | 'exercises' | 'bodyweight' | 'routines' | 'friends' | 'profile' | 'chat' | 'evaluation' | 'wrapped', tab?: number) => void;
  onServiceUnavailable?: () => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({ setView, onServiceUnavailable }) => {
  const { t, language } = useLanguage();
  const { fetchWithAuth, idToken } = useAuth();

  const { activeWorkout } = useActiveWorkout();
  const inProgressDate = activeWorkout?.startedAt.split('T')[0] ?? null;

  // Get all workout-related state and handlers from WorkoutContext
  const {
    selectedDate,
    setSelectedDate,
    calendarMonth,
    setCalendarMonth,
    workouts,
    workoutDays,
    programDays,
    currentSetForDialog,
    setCurrentSetForDialog,
    handleAddSet,
    handleEditSetTrigger,
    handleSaveEditSet,
    handleCancelEditSet,
    workoutLoading,
    workoutError,
    handleOpenDeleteDialog,
    deleteDialogOpen,
    handleCancelDelete,
    handleConfirmDelete,
    handleDeleteWorkoutDetail,
    handleQuickAddSet,
    handleQuickEditSet,
    refreshWorkoutsForDate,
    refreshProgramDays,
    isSavingSet,
    todaysWorkouts,
  } = useWorkout();

  // Refresh program session dots when CalendarView mounts (e.g. navigating back from Programs tab)
  useEffect(() => {
    refreshProgramDays();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get all bodyweight-related state and handlers from BodyWeightContext
  const {
    bodyWeight,
    fatPercentage,
    waistCircumference,
    previousBodyWeight,
    bodyWeightLoading,
    bodyWeightError,
    handleLogBodyWeight,
    bodyWeightDays,
  } = useBodyWeight();

  // Get exercises from ExerciseContext
  const { exercises, setExercises } = useExercise();

  const [addEntryDialogOpen, setAddEntryDialogOpen] = useState(false);
  const [addDialogFormState, setAddDialogFormState] = useState<WorkoutEntryFormState>(defaultNewWorkoutFormState);

  const [bodyWeightDialogOpen, setBodyWeightDialogOpen] = useState(false);

  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analysisPopupOpen, setAnalysisPopupOpen] = useState(false);
  const [analysisResultText, setAnalysisResultText] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [cloneWorkoutsDialogOpen, setCloneWorkoutsDialogOpen] = useState(false);
  const [isCloningWorkouts, setIsCloningWorkouts] = useState(false);

  const [importRoutineDialogOpen, setImportRoutineDialogOpen] = useState(false);

  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);

  const [suggestedCloneDate, setSuggestedCloneDate] = useState<Date | null>(null);

  const [statsExpanded, setStatsExpanded] = useState(true);

  const [programSessions, setProgramSessions] = useState<ProgramSessionForDate[]>([]);

  useEffect(() => {
    if (!selectedDate || !idToken) { setProgramSessions([]); return; }
    ProgramApiService.getProgramSessionsForDate(getDateKey(selectedDate))
      .then(setProgramSessions)
      .catch(() => setProgramSessions([]));
  }, [selectedDate, idToken]);

  const refreshProgramSessions = useCallback(() => {
    if (!selectedDate || !idToken) return;
    ProgramApiService.getProgramSessionsForDate(getDateKey(selectedDate))
      .then(setProgramSessions)
      .catch(() => setProgramSessions([]));
  }, [selectedDate, idToken]);

  const handleWorkoutSessionLogged = useCallback(() => {
    refreshProgramDays();
    refreshProgramSessions();
  }, [refreshProgramDays, refreshProgramSessions]);

  const handleOpenAddEntryDialog = () => {
    setCurrentSetForDialog(defaultNewWorkoutFormState);
    setAddDialogFormState(defaultNewWorkoutFormState);
    setAddEntryDialogOpen(true);
  };

  const handleCloseAddEntryDialog = () => {
    setAddEntryDialogOpen(false);
    setCurrentSetForDialog(null);
  };

  const handleAddDialogFormChange = (field: keyof WorkoutEntryFormState, value: any) => {
    const newFormState = { ...addDialogFormState, [field]: value };
    setAddDialogFormState(newFormState);
  };

  const handleAddDialogSubmit = () => {
    handleAddSet(addDialogFormState);
    handleCloseAddEntryDialog();
  };

  const handleCreateExercise = async (name: string): Promise<Exercise | null> => {
    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });

      if (response.ok) {
        const newExercise = await response.json();
        const exercisesRes = await fetchWithAuth(`${API_URL}/exercises`);
        const exercisesData = await exercisesRes.json();
        setExercises(exercisesData);
        return newExercise;
      } else if (response.status === 409) {
        // Exercise already exists, find it in the current list
        const existingExercise = exercises.find(ex => ex.name.toLowerCase() === name.toLowerCase());
        return existingExercise || null;
      } else {
        console.error('Failed to create exercise');
        return null;
      }
    } catch (error: any) {
      console.error('Error creating exercise:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
      return null;
    }
  };

  const handleOpenAnalysisDialog = () => setAnalysisDialogOpen(true);
  const handleCloseAnalysisDialog = () => setAnalysisDialogOpen(false);

  const handleAnalyzeSubmit = async (startDate: string, endDate: string, goal: string) => {
    setIsAnalyzing(true);
    setAnalysisDialogOpen(false);
    setAnalysisPopupOpen(true);
    setAnalysisResultText(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/workouts/analysis?startDate=${startDate}&endDate=${endDate}&goal=${encodeURIComponent(goal)}&language=${language}`
      );
      if (!response.ok) {
        throw new Error('Failed to analyze workouts');
      }
      const data = await response.json();
      setAnalysisResultText(data.analysis);
    } catch (error: any) {
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setAnalysisResultText(error.message || 'Failed to analyze workouts. Please try again later.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleOpenBodyWeightDialog = () => {
    setBodyWeightDialogOpen(true);
  };

  const handleCloseBodyWeightDialog = () => {
    setBodyWeightDialogOpen(false);
  };

  const handleSaveBodyWeightAndCloseDialog = async (data: { weight: string; fatPercentage: string; waistCircumference: string }) => {
    const success = await handleLogBodyWeight(data);
    if (success) {
      handleCloseBodyWeightDialog();
    }
    return success;
  };

  const handleOpenCloneWorkoutsDialog = () => {
    setCloneWorkoutsDialogOpen(true);
  };

  const handleCloseCloneWorkoutsDialog = () => {
    setCloneWorkoutsDialogOpen(false);
  };

  const handleOpenImportRoutineDialog = () => {
    setImportRoutineDialogOpen(true);
  };

  const handleCloseImportRoutineDialog = () => {
    setImportRoutineDialogOpen(false);
  };

  const handleOpenRecommendationDialog = () => {
    setRecommendationDialogOpen(true);
  };

  const handleCloseRecommendationDialog = () => {
    setRecommendationDialogOpen(false);
  };

  const handleRecommendationImportSuccess = () => {
    // Refresh workouts for the current date after successful import
    if (refreshWorkoutsForDate && selectedDate) {
      refreshWorkoutsForDate(selectedDate);
    }
  };

  const handleCloneWorkouts = async (sourceDate: string, workoutsToClone: WorkoutSet[]) => {
    if (!selectedDate) return;

    setIsCloningWorkouts(true);
    try {
      const targetDateKey = getDateKey(selectedDate);

      // Clone each workout set
      for (const workout of workoutsToClone) {
        const exerciseId = workout.exercise_id || exercises.find(ex => ex.name === workout.exercise)?.id;
        if (!exerciseId) continue;

        const formattedDetails = workout.details.map(detail => ({
          series: detail.series,
          reps: detail.reps,
          weight: detail.weight
        }));

        const response = await fetchWithAuth(`${API_URL}/workouts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: targetDateKey,
            exerciseId: exerciseId,
            comment: workout.comment || null,
            details: formattedDetails
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to clone workout');
        }
      }

      // Refresh workouts for the current date if callback is provided
      if (refreshWorkoutsForDate) {
        refreshWorkoutsForDate(selectedDate);
      }

      handleCloseCloneWorkoutsDialog();
    } catch (error: any) {
      console.error('Error cloning workouts:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setIsCloningWorkouts(false);
    }
  };

  const handleImportFromRoutine = async (routineId: number, dayId: number) => {
    if (!selectedDate) return;

    try {
      const targetDateKey = getDateKey(selectedDate);

      // Import all exercises from the routine day with a single API call
      const response = await fetchWithAuth(`${API_URL}/workouts/import-from-routine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: targetDateKey,
          routineId: routineId,
          dayId: dayId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to import exercises from routine');
      }

      // Refresh workouts for the current date
      if (refreshWorkoutsForDate) {
        refreshWorkoutsForDate(selectedDate);
      }

      handleCloseImportRoutineDialog();
    } catch (error: any) {
      console.error('Error importing from routine:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
      // Let the dialog handle error display
      throw error;
    }
  };

  // Helper function to get relative day name (e.g., "last Monday")
  const getRelativeDayName = (date: Date): string => {
    const dayNames = language === 'es'
      ? ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[date.getDay()];
  };

  // Update suggested clone date when dependencies change
  useEffect(() => {
    let isMounted = true;

    const checkSuggestedDate = async () => {
      if (!selectedDate || todaysWorkouts.length > 0) {
        if (isMounted) {
          setSuggestedCloneDate(null);
        }
        return;
      }

      // Check 7 days ago, then 8 days ago, then 6 days ago
      const daysToCheck = [7, 8, 6];

      for (const daysAgo of daysToCheck) {
        const checkDate = new Date(selectedDate);
        checkDate.setDate(checkDate.getDate() - daysAgo);
        const dateKey = getDateKey(checkDate);

        try {
          const response = await fetchWithAuth(`${API_URL}/workouts?date=${dateKey}`);
          if (response.ok) {
            const data = await response.json();
            if (data && data.length > 0) {
              if (isMounted) {
                setSuggestedCloneDate(checkDate);
              }
              return;
            }
          }
        } catch (error) {
          console.error(`Error checking workouts for ${dateKey}:`, error);
          // Continue to next date
        }
      }

      if (isMounted) {
        setSuggestedCloneDate(null);
      }
    };

    checkSuggestedDate();

    return () => {
      isMounted = false;
    };
  }, [selectedDate, todaysWorkouts, fetchWithAuth]);

  // Check if current day has no workouts to enable/disable clone button
  const hasWorkoutsToday = todaysWorkouts.length > 0;

  return (
    <Box sx={{ mt: 4 }}>
      {/* Wrapped Button + Stats Section */}
      <WorkoutStatsSection
        onNavigateToWrapped={() => setView('wrapped')}
        onServiceUnavailable={onServiceUnavailable}
        statsExpanded={statsExpanded}
        setStatsExpanded={setStatsExpanded}
      />

      {/* Program workout widget — start or resume a workout from the calendar */}
      <CalendarWorkoutWidget onSessionLogged={handleWorkoutSessionLogged} />

      {/* Calendar Widget */}
      <WorkoutCalendar
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        calendarMonth={calendarMonth}
        setCalendarMonth={setCalendarMonth}
        workouts={workouts}
        workoutDays={workoutDays}
        bodyWeightDays={bodyWeightDays}
        programDays={programDays}
        inProgressDate={inProgressDate}
        language={language}
      />

      {/* Timer, AI Analysis, Smart Actions, Main Toolbar, Menu */}
      <WorkoutActionSection
        selectedDate={selectedDate}
        bodyWeight={bodyWeight}
        previousBodyWeight={previousBodyWeight}
        isAnalyzing={isAnalyzing}
        onOpenAnalysisDialog={handleOpenAnalysisDialog}
        suggestedCloneDate={suggestedCloneDate}
        hasWorkoutsToday={hasWorkoutsToday}
        isCloningWorkouts={isCloningWorkouts}
        onOpenCloneDialog={handleOpenCloneWorkoutsDialog}
        onOpenAddEntryDialog={handleOpenAddEntryDialog}
        onOpenBodyWeightDialog={handleOpenBodyWeightDialog}
        onOpenImportRoutineDialog={handleOpenImportRoutineDialog}
        onOpenRecommendationDialog={handleOpenRecommendationDialog}
        getRelativeDayName={getRelativeDayName}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" align="center" gutterBottom>
          {selectedDate
            ? `${t('calendar.workoutsFor')} ${selectedDate.toLocaleDateString()}`
            : t('calendar.selectDate')
          }
        </Typography>

        <WorkoutList
          workouts={todaysWorkouts}
          workoutLoading={workoutLoading}
          workoutError={workoutError}
          currentSetForDialog={currentSetForDialog}
          setCurrentSetForDialog={setCurrentSetForDialog}
          handleEditSetTrigger={handleEditSetTrigger}
          handleSaveEditSet={handleSaveEditSet}
          handleCancelEditSet={handleCancelEditSet}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          deleteDialogOpen={deleteDialogOpen}
          handleCancelDelete={handleCancelDelete}
          handleConfirmDelete={handleConfirmDelete}
          selectedDate={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
          exercises={exercises}
          isSavingSet={isSavingSet}
          handleDeleteWorkoutDetail={handleDeleteWorkoutDetail}
          onCreateExercise={handleCreateExercise}
          onQuickAddSet={handleQuickAddSet}
          onQuickEditSet={handleQuickEditSet}
        />

        {programSessions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {programSessions.map((s) => (
              <ProgramSessionCard key={s.session_id} session={s} />
            ))}
          </Box>
        )}
      </Box>

      {addEntryDialogOpen && (
        <WorkoutEntryDialog
          open={addEntryDialogOpen}
          onClose={handleCloseAddEntryDialog}
          mode={'add'}
          exercises={exercises}
          initialFormValues={addDialogFormState}
          onFormChange={handleAddDialogFormChange}
          onSubmit={handleAddDialogSubmit}
          isSavingSet={isSavingSet}
          onCreateExercise={handleCreateExercise}
        />
      )}

      <AnalysisDialog
        open={analysisDialogOpen}
        onClose={handleCloseAnalysisDialog}
        onAnalyze={handleAnalyzeSubmit}
        selectedDate={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
        isLoading={isAnalyzing}
        onServiceUnavailable={onServiceUnavailable}
      />

      <AnalysisPopup
        open={analysisPopupOpen}
        onClose={() => setAnalysisPopupOpen(false)}
        analysis={analysisResultText}
        isLoading={isAnalyzing}
      />

      {selectedDate && (
        <AddBodyWeightDialog
          open={bodyWeightDialogOpen}
          onClose={handleCloseBodyWeightDialog}
          currentInitialWeight={bodyWeight}
          currentInitialFatPercentage={fatPercentage}
          currentInitialWaistCircumference={waistCircumference}
          onSave={handleSaveBodyWeightAndCloseDialog}
          isSaving={bodyWeightLoading}
          saveError={bodyWeightError}
          selectedDate={selectedDate}
        />
      )}

      {selectedDate && (
        <CloneWorkoutsDialog
          open={cloneWorkoutsDialogOpen}
          onClose={handleCloseCloneWorkoutsDialog}
          onSave={handleCloneWorkouts}
          currentDate={selectedDate}
          isSaving={isCloningWorkouts}
          initialDate={suggestedCloneDate}
        />
      )}

      {selectedDate && (
        <ImportRoutineDialog
          open={importRoutineDialogOpen}
          onClose={handleCloseImportRoutineDialog}
          onImport={handleImportFromRoutine}
          selectedDate={selectedDate}
          fetchWithAuth={fetchWithAuth}
          onServiceUnavailable={onServiceUnavailable}
        />
      )}

      {selectedDate && (
        <RecommendationDialog
          open={recommendationDialogOpen}
          onClose={handleCloseRecommendationDialog}
          selectedDate={selectedDate}
          onServiceUnavailable={onServiceUnavailable}
          onImportSuccess={handleRecommendationImportSuccess}
          exercises={exercises}
        />
      )}
    </Box>
  );
}; 